

import { Card, CardType, GameState, GamePhase, Player, PreparedCard, CardColor, ActivationAttempt, Tower, HeroId, ClassId } from '../types';
import { calculateFamePoints, FHGame } from './fhCalculator'
import { ensureDealingDeck, drawFromDealingDeck, shuffle, createColorDecks, createStashDeck } from './decks';
import { DECK_COMPOSITION, STASH_DECK_COMPOSITION, INITIAL_HEALTH, MAX_PREPARED_CARDS, ABSOLUTE_MAX_PREPARED_CARDS } from '../cardCatalog';
import { startStashChoicePhase, transitionToPreparationPhase, startCombat, startNewRound, assignVictoryPoint } from './phases';
import { getCardEffect, isShieldEffect, checkAndTriggerLifeCharger, isImmuneToDamage, applyHealthLoss } from './effects';
import { attemptActivation, attemptShieldActivation, astridHeroBlast, counterActivation } from './activations';
import { isValidCounter } from './ai';
import { generateTowerId } from './towers';
import { getEffectiveHero } from './heroes';
import { v4 as uuidv4 } from 'uuid';
import { computeStateChecksum } from './debug';
import { xmur3, mulberry32 } from './random';
import { getValidCounterblastActivationIds } from './damageRules';


export { isImmuneToDamage } from './effects';
export { isShieldEffect } from './effects';
export { isTargetableEffect, isActivationChainTargetable } from './effects';
export { checkAndTriggerLifeCharger } from './effects';


const makeDeterministicId = (seed: string, ns: string, idx: number): string => {
    const gen = xmur3(`${seed}_${ns}_${idx}`)
    return gen().toString()
}



export { ensureDealingDeck, drawFromDealingDeck } from './decks';

export { startStashChoicePhase, getAvailablePrepareSlots } from './phases';


export const getEffectiveEssenceCostForPlayer = (card: Card, player: Player): number => {
    const base = card.essenceCost ?? 0;
    if (base && card.subType === 'Artifact' && player.playerClass === 'OCCULTIST') {
        return Math.floor(base / 2);
    }
    return base;
};

export const initGame = (initialSeed?: string, playerData?: [{ name: string, hero?: any, playerClass?: any, isAI?: boolean }, { name: string, hero?: any, playerClass?: any, isAI?: boolean }]): GameState => {
    // DEBUG: Track game initialization for Nyrix persistence bug
    console.log('[DEBUG-NYRIX] initGame called:', {
        hasSeed: !!initialSeed,
        hasPlayerData: !!playerData,
        p1Hero: playerData?.[0]?.hero,
        p2Hero: playerData?.[1]?.hero
    })
    const seed = initialSeed || uuidv4();

    const seedGen = xmur3(seed + '_initial_turn_order');
    const rand = mulberry32(seedGen());
    const firstTowerPickerId = Math.floor(rand() * 2) + 1; // Returns 1 or 2
    const firstNightPlayerId = firstTowerPickerId === 1 ? 2 : 1; // Opposite player goes first at night


    const colorDecks = createColorDecks(seed);

    const player1Hand: Card[] = [];
    const player2Hand: Card[] = [];

    Object.values(CardColor).forEach(color => {
        const p1Card = colorDecks[color].pop();
        if (p1Card) {
            p1Card.ownerId = 1;
            player1Hand.push(p1Card);
        }
        const p2Card = colorDecks[color].pop();
        if (p2Card) {
            p2Card.ownerId = 2;
            player2Hand.push(p2Card);
        }
    });

    const p1Data = playerData ? playerData[0] : { name: 'Player', hero: undefined, playerClass: undefined };
    const p2Data = playerData ? playerData[1] : { name: 'AI', hero: undefined, playerClass: undefined };

    const players: [Player, Player] = [
        {
            id: 1,
            name: p1Data.name,
            health: INITIAL_HEALTH,
            maxHealth: INITIAL_HEALTH,
            essence: 0,
            maxPrepareSlots: MAX_PREPARED_CARDS,
            disruptedSlots: 0,
            hand: shuffle(player1Hand, seed + '_player1_hand'),
            prepared: [],
            isAI: false,
            effectCards: [],
            towers: [],
            shield: 0,
            resolved: [],
            capturedTowerThisTurn: false,
            changedTowerColorThisTurn: false,
            bonusDraw: 0,
            hasPassedActiveTurn: false,
            hero: p1Data.hero,
            playerClass: p1Data.playerClass,
            hasChosenHeroAndClass: !!(p1Data.hero && p1Data.playerClass),
            famePoints: 0,
        },
        {
            id: 2,
            name: p2Data.name,
            health: INITIAL_HEALTH,
            maxHealth: INITIAL_HEALTH,
            essence: 0,
            maxPrepareSlots: MAX_PREPARED_CARDS,
            disruptedSlots: 0,
            hand: shuffle(player2Hand, seed + '_player2_hand'),
            prepared: [],
            isAI: !playerData || !!playerData[1]?.isAI,
            effectCards: [],
            towers: [],
            shield: 0,
            resolved: [],
            capturedTowerThisTurn: false,
            changedTowerColorThisTurn: false,
            bonusDraw: 0,
            hasPassedActiveTurn: false,
            hero: p2Data.hero,
            playerClass: p2Data.playerClass,
            hasChosenHeroAndClass: !!(p2Data.hero && p2Data.playerClass),
            famePoints: 0,
        },
    ];

    const initialState: GameState = {
        players,
        phase: GamePhase.TowerChoice,
        round: 1,
        log: ['t:log.roundStart|round=1'],
        winner: null,
        colorDecks,
        dealingDeck: [],
        stashDeck: createStashDeck(seed),
        stashDiscard: [],
        burnedStashCards: [],
        stashChoiceOptions: [],
        turnPlayerId: firstTowerPickerId,
        firstPlayerIdForNight: firstNightPlayerId,
        heroClassTurnId: firstNightPlayerId,
        consecutivePasses: 0,
        availableTowers: Object.values(CardColor),
        drawEvents: [],
        discardMode: null,
        activations: [],
        revealedCard: null,
        seed,
        eliminatedCards: [],
        battleHistory: [],
        mapPoints: {},
        mapControl: {},
        pendingVictory: null,
    };

    return initialState;
};

export const chooseTower = (state: GameState, playerId: number, towerColor: CardColor): GameState => {
    let newState = { ...state };

    // Validate it's this player's turn
    if (state.turnPlayerId !== playerId) {
        return newState;
    }

    const playerIndex = newState.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1 || !newState.availableTowers.includes(towerColor)) return newState;

    const newPlayer = { ...newState.players[playerIndex] };
    if (newPlayer.towers.length > 0) return newState;

    const allExistingTowers = [...newState.players[0].towers, ...newState.players[1].towers];
    const newTowerId = generateTowerId(towerColor, allExistingTowers);

    newPlayer.towers = [...newPlayer.towers, { color: towerColor, id: newTowerId }];

    const newPlayers = [...newState.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    let newAvailableTowers = newState.availableTowers.filter(t => t !== towerColor);

    newState = { ...newState, players: newPlayers, availableTowers: newAvailableTowers };

    // Log this player's choice
    const playerName = newPlayer.name;
    newState.log = [...newState.log, `t:log.towerChose|playerName=${playerName}|towerColor=${towerColor}`];

    // Check if both players have chosen
    const bothPlayersHaveTowers = newPlayers[0].towers.length > 0 && newPlayers[1].towers.length > 0;

    if (bothPlayersHaveTowers) {
        const heroClassComplete = newPlayers.every(p => p.hasChosenHeroAndClass);
        if (heroClassComplete) {
            return startStashChoicePhase(newState);
        }
        return {
            ...newState,
            turnPlayerId: newState.turnPlayerId,
        };
    } else {
        // Switch to other player's turn
        const nextPlayerId = playerId === 1 ? 2 : 1;
        return {
            ...newState,
            turnPlayerId: nextPlayerId,
        };
    }
};

export const chooseHeroClass = (state: GameState, playerId: number, hero: HeroId, playerClass: ClassId): GameState => {
    // DEBUG: Track hero selection for Nyrix persistence bug
    console.log('[DEBUG-NYRIX] chooseHeroClass called:', {
        playerId,
        hero,
        playerClass,
        phase: state.phase,
        round: state.round
    })
    let newState = { ...state };
    const playerIndex = newState.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return newState;
    if (newState.heroClassTurnId && newState.heroClassTurnId !== playerId) return newState;
    const newPlayer = { ...newState.players[playerIndex] };
    newPlayer.hero = hero;
    newPlayer.playerClass = playerClass;
    newPlayer.hasChosenHeroAndClass = true;
    if (hero === 'ASTRID') {
        newPlayer.astridCharges = {
            [CardColor.Red]: 0,
            [CardColor.Pink]: 0,
            [CardColor.Green]: 0,
            [CardColor.Black]: 0,
            [CardColor.Purple]: 0,
            [CardColor.Yellow]: 0,
        };
    }
    if (hero === 'NORIM') {
        newPlayer.norimCharges = 0;
    }
    const newPlayers = [...newState.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;
    const opponent = playerId === 1 ? newPlayers[1] : newPlayers[0];
    let newLog = [...newState.log, `t:log.heroClassSelected|playerName=${newPlayer.name}|hero=${hero}|playerClass=${playerClass}`];
    const bothHaveTowers = newPlayers[0].towers.length > 0 && newPlayers[1].towers.length > 0;
    const heroClassComplete = newPlayers.every(p => p.hasChosenHeroAndClass);
    if (bothHaveTowers && heroClassComplete) {
        return startStashChoicePhase({ ...newState, players: newPlayers, log: newLog });
    }
    const nextHeroTurn = playerId === 1 ? 2 : 1;
    return { ...newState, players: newPlayers, log: newLog, heroClassTurnId: nextHeroTurn };
};

export const chooseLorianClass = (state: GameState, playerId: number, playerClass: ClassId): GameState => {
    let newState = { ...state };
    const playerIndex = newState.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return newState;

    const player = newState.players[playerIndex];
    if (player.hero !== 'LORIAN') return newState;

    const newPlayer = { ...player, playerClass };
    const newPlayers = [...newState.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    const newLog = [...newState.log, `t:log.lorianChoiceRefined|playerName=${player.name}|playerClass=${playerClass}`];

    return startStashChoicePhase({
        ...newState,
        players: newPlayers,
        log: newLog
    });
};


export const makeStashChoice = (state: GameState, playerId: number, chosenCardId: string, fromBurned?: boolean): GameState => {
    // IMPORTANT: Deep copy arrays to prevent mutating original state (critical for multiplayer sync)
    let newStashDiscard = [...state.stashDiscard];
    let newBurnedStashCards = [...state.burnedStashCards];
    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    let chosenCard: Card | undefined;
    let optionsForPlayer = state.stashChoiceOptions[playerId] || [];
    let remainingCardsForPlayer = optionsForPlayer;

    if (fromBurned) {
        const cardIndex = newBurnedStashCards.findIndex(c => c.id === chosenCardId);
        if (cardIndex === -1) return state;
        // Deep copy the card to avoid mutating original
        chosenCard = { ...newBurnedStashCards[cardIndex] };
        // Remove from burned pile
        newBurnedStashCards = [
            ...newBurnedStashCards.slice(0, cardIndex),
            ...newBurnedStashCards.slice(cardIndex + 1)
        ];
        // If picking from burned, the optionsForPlayer (from stashChoiceOptions) are NOT affected by this pick.
        // So remainingCardsForPlayer stays as the original optionsForPlayer.

        // [REWARD] Log Ferrim hero power usage
        state = { ...state, log: [...state.log, `[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=FERRIM_BURNED_CHOICE`] };
    } else {
        const foundCard = optionsForPlayer.find(c => c.id === chosenCardId);
        if (!foundCard) {
            // STATE DIVERGENCE DETECTED: Card not in this player's options
            // This happens when clients have different stash deck states
            console.warn(`[makeStashChoice] State divergence: Card ${chosenCardId} not in Player ${playerId}'s options. Regenerating options.`);

            // Regenerate fresh options from the current stash deck
            let newStashDeck = [...state.stashDeck];
            const perPlayerCount = 5;

            if (newStashDeck.length < perPlayerCount) {
                // Not enough cards - use what we have
                console.warn(`[makeStashChoice] Only ${newStashDeck.length} cards available for regeneration`);
            }

            const newOptionsForPlayer = newStashDeck.splice(0, Math.min(perPlayerCount, newStashDeck.length));
            const newOptionsMap = { ...(state.stashChoiceOptions || {}) } as Record<number, Card[]>;
            newOptionsMap[playerId] = newOptionsForPlayer;

            console.log(`[makeStashChoice] Regenerated ${newOptionsForPlayer.length} new options for Player ${playerId}`);

            return {
                ...state,
                stashDeck: newStashDeck,
                stashChoiceOptions: newOptionsMap,
                log: [...state.log, `t:log.stashRefresh|playerName=${state.players[playerIndex].name}`],
            };
        }
        // Deep copy the card to avoid mutating original
        chosenCard = { ...foundCard };
        remainingCardsForPlayer = optionsForPlayer.filter(c => c.id !== chosenCardId);
    }

    chosenCard.ownerId = playerId;

    const newPlayer = { ...state.players[playerIndex] };
    newPlayer.hand = [...newPlayer.hand, chosenCard];
    const newPlayers = [...state.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    const picksMap = { ...(state.stashChoicePicksRemaining || {}) } as Record<number, number>;
    const picksRemaining = picksMap[playerId] || 0;
    const picksAfter = Math.max(0, picksRemaining - 1);

    const optionsMap = { ...(state.stashChoiceOptions || {}) } as Record<number, Card[]>;

    // Always update options map for the player, even if they picked from burned (to keep state consistent or just re-assign same options)
    optionsMap[playerId] = remainingCardsForPlayer;

    if (picksAfter > 0) {
        picksMap[playerId] = picksAfter;
        const newLog = [...state.log, `t:log.oneMoreStash|playerName=${newPlayer.name}`];
        return {
            ...state,
            players: newPlayers,
            stashDiscard: newStashDiscard,
            burnedStashCards: newBurnedStashCards,
            stashChoiceOptions: optionsMap,
            stashChoicePicksRemaining: picksMap,
            log: newLog,
        };
    } else {
        // Player finished picking - save their unchosen cards to stashDiscard BEFORE clearing
        const unchosenCards = remainingCardsForPlayer;
        newStashDiscard = [...newStashDiscard, ...unchosenCards];
        delete picksMap[playerId];
        optionsMap[playerId] = [];
    }

    const otherId = playerId === 1 ? 2 : 1;
    const otherOptions = optionsMap[otherId] || [];

    // DEBUG: Log state for troubleshooting stuck games
    console.log(`[makeStashChoice] Player ${playerId} finished. Other player (${otherId}) options: ${otherOptions.length}, picks remaining:`, picksMap);

    // Single-player: auto-resolve AI picks from its own options
    // Only if P1 is Human (not AI). If P1 is AI, it's a simulation/headless run, let the runner handle P2.
    const isSinglePlayer = newPlayers[1].isAI && !newPlayers[0].isAI;
    if (isSinglePlayer && playerId === 1) {
        let s: GameState = { ...state, players: newPlayers, stashDiscard: newStashDiscard, burnedStashCards: newBurnedStashCards, stashChoiceOptions: optionsMap, stashChoicePicksRemaining: picksMap };
        if (otherOptions.length > 0) {
            const attackCard = otherOptions.find(c => c.type === CardType.Attack);
            const aiChoice = attackCard || otherOptions[0];
            s = makeStashChoice(s, 2, aiChoice.id);
            const sOtherOptions = s.stashChoiceOptions[2] || [];
            const aiPlayer = s.players[1];
            if (aiPlayer.playerClass === 'MAGE' && sOtherOptions.length > 0) {
                const attackCard2 = sOtherOptions.find(c => c.type === CardType.Attack);
                const aiChoice2 = attackCard2 || sOtherOptions[0];
                s = makeStashChoice(s, 2, aiChoice2.id);
            }
        }
        // After AI resolution, transition (unchosen cards already saved by each player's pick)
        const remP1 = (s.stashChoiceOptions[1] || []).length;
        const remP2 = (s.stashChoiceOptions[2] || []).length;
        if (remP1 === 0 && remP2 === 0) {
            console.log(`[makeStashChoice] AI mode: Both done, transitioning to Preparation`);
            return transitionToPreparationPhase({ ...s, stashChoiceOptions: {}, stashChoicePicksRemaining: undefined });
        }
        return s;
    }

    // For PvP: Check if EITHER player still has picks remaining
    const myPicksRemaining = picksMap[playerId] || 0;
    const otherPicksRemaining = picksMap[otherId] || 0;
    console.log(`[makeStashChoice] PvP check: Player ${playerId} picks=${myPicksRemaining}, Player ${otherId} picks=${otherPicksRemaining}`);

    // If EITHER player still has picks remaining, stay in StashChoice
    if (myPicksRemaining > 0 || otherPicksRemaining > 0) {
        console.log(`[makeStashChoice] PvP: Someone still has picks remaining, staying in StashChoice`);
        return {
            ...state,
            players: newPlayers,
            stashDiscard: newStashDiscard,
            burnedStashCards: newBurnedStashCards,
            stashChoiceOptions: optionsMap,
            stashChoicePicksRemaining: picksMap,
        };
    }

    // Both players done - transition to Preparation
    console.log(`[makeStashChoice] PvP: Both done, transitioning to Preparation`);
    return transitionToPreparationPhase({
        ...state,
        players: newPlayers,
        stashDiscard: newStashDiscard,
        burnedStashCards: newBurnedStashCards,
        stashChoiceOptions: {},
        stashChoicePicksRemaining: undefined,
    });
};

// AI auto-resolution for overflow (Legacy - now handled via phase events in App.tsx)
export const resolveOverflowForAI = (state: GameState): GameState => {
    return state; // Deprecated: Logic moved to App.tsx and gameLogic.ts returnLockedCardFromOverflow
};

export { transitionToPreparationPhase } from './phases';

export const returnLockedCardFromOverflow = (state: GameState, cardId: string): GameState => {
    if (state.phase !== GamePhase.LockedCardOverflow || !state.overflowPlayerId || !state.overflowMaxSlots) {
        return state;
    }

    const playerIndex = state.players.findIndex(p => p.id === state.overflowPlayerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];
    const cardIndex = player.prepared.findIndex(pc => pc.card.id === cardId && pc.lockedState);
    if (cardIndex === -1) return state;

    const lockedCards = player.prepared.filter(pc => pc.lockedState);
    const excessCount = lockedCards.length - state.overflowMaxSlots;

    if (excessCount <= 0) return state;

    const card = player.prepared[cardIndex].card;

    const newPlayer = { ...player };
    newPlayer.prepared = [
        ...player.prepared.slice(0, cardIndex),
        ...player.prepared.slice(cardIndex + 1)
    ];
    newPlayer.hand = [...player.hand, card];

    const newPlayers = [...state.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    const newLog = [...state.log, `${player.name}'s locked ${getCardEffect(player.prepared[cardIndex]).type} returns to hand (slot overflow).`];

    // Check if overflow is resolved
    const remainingLocked = newPlayer.prepared.filter(pc => pc.lockedState);
    if (remainingLocked.length <= state.overflowMaxSlots) {
        // Overflow resolved, continue to preparation phase
        return transitionToPreparationPhase({
            ...state,
            players: newPlayers,
            log: newLog,
            overflowPlayerId: undefined,
            overflowMaxSlots: undefined,
        });
    }

    // Still need to return more cards
    return {
        ...state,
        players: newPlayers,
        log: newLog,
    };
};

export const getLockedCardsInOverflow = (state: GameState): PreparedCard[] => {
    if (state.phase !== GamePhase.LockedCardOverflow || !state.overflowPlayerId) {
        return [];
    }

    const player = state.players.find(p => p.id === state.overflowPlayerId);
    if (!player) return [];

    return player.prepared.filter(pc => pc.lockedState);
};

export const prepareCard = (state: GameState, playerId: number, cardId: string, rotation: number): GameState => {
    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];
    if (player.prepared.some(pc => pc.card.id === cardId)) {
        return state;
    }
    const cardIndex = player.hand.findIndex(c => c.id === cardId);
    if (cardIndex === -1) return state;

    const card = player.hand[cardIndex];
    const isDay = state.phase === GamePhase.Preparation;
    const isWarrior = player.playerClass === 'WARRIOR';
    const isArmoryStashCard = !!card.essenceCost && card.subType === 'Armory';
    const hasPreparedArmory = player.prepared.some(pc => !!pc.card.essenceCost && pc.card.subType === 'Armory');
    const baseSlots = player.maxPrepareSlots - player.disruptedSlots;
    const warriorBonus = (isDay && isWarrior && (hasPreparedArmory || isArmoryStashCard)) ? 1 : 0;
    const opponent = state.players.find(p => p.id !== playerId)!;
    const effectiveHero = getEffectiveHero(player, opponent);
    const heroBonus = effectiveHero === 'EROK_BURU' ? Math.floor((player.maxHealth - player.health) / 2) : 0;
    const availableSlots = Math.min(ABSOLUTE_MAX_PREPARED_CARDS, baseSlots + warriorBonus + heroBonus);
    if (player.prepared.length >= availableSlots) return state;

    const preparedCard: PreparedCard = { card, rotation };

    const newPlayer = { ...player };
    newPlayer.hand = player.hand.filter(c => c.id !== cardId);
    newPlayer.prepared = [...newPlayer.prepared, preparedCard];

    const newPlayers = [...state.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    return { ...state, players: newPlayers };
};

export const mageCastSpellFromHand = (state: GameState, playerId: number, cardId: string, rotation: number = 0, targetId?: number, targetActivationId?: string): GameState => {
    if (state.phase !== GamePhase.Combat) return state;
    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;
    const player = state.players[playerIndex];
    if (player.playerClass !== 'MAGE') return state;
    if (player.prepared.some(pc => pc.card.id === cardId)) {
        return state;
    }
    const handIdx = player.hand.findIndex(c => c.id === cardId);
    if (handIdx === -1) return state;
    const card = player.hand[handIdx];
    if (!card.essenceCost || card.subType !== 'Spell') return state;
    // Seal of Silence prevents casting Spell cards from hand during this night
    const silencedNow = player.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === state.round);
    if (silencedNow) return state;
    const mageSlotsUsed = player.prepared.filter(pc => pc.mageHandCast).length;
    if (mageSlotsUsed >= 3) return state;
    if (player.essence < card.essenceCost) return state;
    const currentActivation = state.activations.length > 0 ? state.activations[state.activations.length - 1] : null;
    const responderId = currentActivation ? (currentActivation.initiatorId === 1 ? 2 : 1) : null;
    const isResponderTurn = !!currentActivation && currentActivation.state === 'PENDING_RESPONSE' && responderId === playerId;
    const isInitiatorTurn = !currentActivation && state.turnPlayerId === playerId;
    if (!isInitiatorTurn && !isResponderTurn) return state;
    const spellEffect = getCardEffect({ card, rotation });
    if (spellEffect.type === CardType.StealTower && player.capturedTowerThisTurn) {
        return { ...state, log: [...state.log, `t:log.alreadyCaptured|playerName=${player.name}`] };
    }
    if (player.hasPassedActiveTurn) {
        if (!isShieldEffect(spellEffect.type) && !card.playableAfterPass) {
            return { ...state, log: [...state.log, `t:log.shieldOnly|playerName=${player.name}`] };
        }
    }
    if (isResponderTurn && spellEffect.type === CardType.Counterblast) {
        const validCounterblastIds = getValidCounterblastActivationIds(state, playerId);
        if (!targetActivationId || !validCounterblastIds.includes(targetActivationId)) {
            return state;
        }
    }
    const newPlayer = { ...player };
    const preparedCard: PreparedCard = { card, rotation, mageHandCast: true };
    newPlayer.hand = player.hand.filter(c => c.id !== cardId);
    newPlayer.prepared = [...player.prepared, preparedCard];
    const newPlayers = [...state.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    // [REWARD] Log Mage class power usage
    const rewardLog = `[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=MAGE_CAST_SPELL`;
    const interimState = { ...state, players: newPlayers, log: [...state.log, rewardLog] };
    if (isResponderTurn) {
        // For Counterblast/Invincible with active chains, use attemptShieldActivation with targetActivationId
        if (card.extendedShield) {
            return attemptShieldActivation(interimState, playerId, cardId, rotation, targetActivationId);
        }
        return counterActivation(interimState, playerId, cardId, rotation, targetActivationId);
    }
    return attemptActivation(interimState, playerId, cardId, rotation, targetId);
};

export const returnCardToHand = (state: GameState, playerId: number, cardId: string): GameState => {
    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];

    const cardIndexInPrepared = player.prepared.findIndex(pc => pc.card.id === cardId);
    if (cardIndexInPrepared === -1) return state;

    const preparedCard = player.prepared[cardIndexInPrepared];

    if (preparedCard.lockedState) {
        const roundsSinceLocked = state.round - preparedCard.lockedState.appliedRound;
        const effectiveDays = roundsSinceLocked + (state.extraDaysInCurrentRound || 0);
        if (effectiveDays < 2) {
            return { ...state, log: [...state.log, `${player.name} cannot return this locked card yet!`] };
        }
    }

    const card = preparedCard.card;
    const isDay = state.phase === GamePhase.Preparation;
    const isWarrior = player.playerClass === 'WARRIOR';
    const isArmoryStashCard = !!card.essenceCost && card.subType === 'Armory';
    if (isDay && isWarrior && isArmoryStashCard) {
        const newPlayer = { ...player };
        const unlockedPrepared = player.prepared.filter(pc => !pc.lockedState);
        const lockedPrepared = player.prepared.filter(pc => pc.lockedState);
        const returnedCards = unlockedPrepared.map(pc => pc.card);
        newPlayer.prepared = lockedPrepared;
        newPlayer.hand = [...player.hand, ...returnedCards];
        const newPlayers = [...state.players] as [Player, Player];
        newPlayers[playerIndex] = newPlayer;
        return { ...state, players: newPlayers, log: [...state.log, `${player.name} unprepared Armory and all other cards were returned to hand.`] };
    }

    const newPlayer = { ...player };
    newPlayer.prepared = [
        ...player.prepared.slice(0, cardIndexInPrepared),
        ...player.prepared.slice(cardIndexInPrepared + 1)
    ];
    // Avoid duplicates in hand
    if (!newPlayer.hand.some(c => c.id === card.id)) {
        newPlayer.hand = [...player.hand, card];
    }

    const newPlayers = [...state.players] as [Player, Player];
    newPlayers[playerIndex] = newPlayer;

    return { ...state, players: newPlayers };
};

// Rotate a prepared card (used when syncing rotation state in multiplayer for Discerning Eye visibility)
export const rotatePreparedCard = (state: GameState, playerId: number, cardId: string, rotation: number): GameState => {
    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];
    const cardIndex = player.prepared.findIndex(pc => pc.card.id === cardId);
    if (cardIndex === -1) return state;

    const newPlayers = [...state.players] as [Player, Player];
    const newPlayer = { ...player };
    const newPrepared = [...player.prepared];
    newPrepared[cardIndex] = { ...newPrepared[cardIndex], rotation };
    newPlayer.prepared = newPrepared;
    newPlayers[playerIndex] = newPlayer;

    return { ...state, players: newPlayers };
};

export { getCardEffect } from './effects';

export { startCombat } from './phases';


const _attemptActivation = (state: GameState, playerId: number, cardId: string, rotation: number, targetId?: number): GameState => {
    if (state.turnPlayerId !== playerId || state.activations.length > 0) return state;

    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];

    let preparedCardData = player.prepared.find(pc => pc.card.id === cardId);

    if (!preparedCardData) {
        if (player.isAI) {
            return passTurn(state, playerId);
        }
        return state;
    }

    if (preparedCardData.card.type === CardType.PlantedBomb) {
        return { ...state, log: [...state.log, `${player.name} cannot use Planted Bomb as a direct action.`] };
    }

    let preparedCard: PreparedCard = { ...preparedCardData, rotation };
    const playersForUpdate: [Player, Player] = JSON.parse(JSON.stringify(state.players));

    const effect = getCardEffect(preparedCard);

    // Rule: After passing, only shield cards (or special cards) can be activated.
    if (player.hasPassedActiveTurn && !isShieldEffect(effect.type) && !preparedCard.card.playableAfterPass) {
        if (player.isAI) {
            return passTurn(state, playerId);
        }
        return { ...state, log: [...state.log, `${player.name} can only play shield cards now!`] };
    }

    if (preparedCardData.lockedState) {
        const roundsSinceLocked = state.round - preparedCardData.lockedState.appliedRound;

        // Night 1 (round when it was applied): Cannot use.
        if (roundsSinceLocked === 0) {
            if (player.isAI) {
                return passTurn(state, playerId);
            }
            return { ...state, log: [...state.log, `${player.name} cannot use this locked card yet!`] };
        }

        // Night 2 onwards: The rotation is fixed. The incoming `rotation` parameter is from the UI, which shouldn't have allowed rotation. We enforce the stored rotation.
        preparedCard = { ...preparedCardData, rotation: preparedCardData.rotation };
    }

    const newPlayers: [Player, Player] = playersForUpdate;
    const attacker = newPlayers.find(p => p.id === playerId)!;
    let newLog = [...state.log];
    const { type } = getCardEffect(preparedCard);
    const isStashCard = !!preparedCard.card.essenceCost;

    if (isStashCard) {
        if (attacker.essence < preparedCard.card.essenceCost!) {
            if (attacker.isAI) {
                return passTurn(state, playerId);
            }
            return { ...state, log: [...newLog, `${attacker.name} does not have enough essence!`] };
        }
        attacker.essence -= preparedCard.card.essenceCost!;
        newLog.push(`${attacker.name} pays ${preparedCard.card.essenceCost} essence.`);
    }

    const newActivation: ActivationAttempt = {
        initiatorId: playerId,
        attackingCard: preparedCard,
        counterChain: [],
        state: 'PENDING_RESPONSE',
        targetId: targetId ?? (playerId === 1 ? 2 : 1),
        resolved: false,
    };

    newLog.push(`t:log.attemptActivate|playerName=${attacker.name}|cardType=${preparedCard.card.faceSlug || getCardEffect(preparedCard).type}`);

    return { ...state, players: newPlayers, log: newLog, activations: [newActivation], consecutivePasses: 0 };
};

const _attemptShieldActivation = (state: GameState, playerId: number, cardId: string, rotation: number): GameState => {
    // Must have an active activation to use a shield card against
    if (state.activations.length === 0) {
        return state;
    }

    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const player = state.players[playerIndex];
    const preparedCardData = player.prepared.find(pc => pc.card.id === cardId);

    // This is for special shields like Counterblast that start a new chain.
    if (!preparedCardData || !preparedCardData.card.extendedShield) {
        return state;
    }

    // --- Special Shield Validation ---
    const cardBeingActivated = preparedCardData.card;
    const specialShieldsToCheck = [
        CardType.Protector,
        CardType.Counterblast,
        CardType.LifeCharger,
        CardType.Invincible
    ];

    const isSpecialShield = specialShieldsToCheck.includes(cardBeingActivated.type) ||
        (cardBeingActivated.type === CardType.Defense && cardBeingActivated.subType === 'Armory'); // This is Ancient Shield

    if (isSpecialShield) {
        // These special shields can only react to the *initial* card of an activation,
        // and only if it's a damage-dealing action.
        const originalActivation = state.activations[0];
        const targetCard = originalActivation.attackingCard;
        const targetEffect = getCardEffect(targetCard);

        const damageDealingTypes = [
            CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
            CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
            CardType.Chromabow
        ];

        // A rotated Protector is also considered a damage dealing card for this purpose.
        const finalRotation = (targetCard.rotation % 360 + 360) % 360;
        const isFlipped = finalRotation > 90 && finalRotation < 270;

        const isTargetDamageDealing = damageDealingTypes.includes(targetEffect.type) ||
            (targetEffect.type === CardType.Protector && isFlipped);

        if (!isTargetDamageDealing) {
            const cardName = cardBeingActivated.type === CardType.Defense && cardBeingActivated.subType === 'Armory' ? 'Ancient Shield' : cardBeingActivated.type;
            return { ...state, log: [...state.log, `t:log.defenseActionOnly|playerName=${player.name}|cardName=${cardName}`] };
        }
    }


    let preparedCard: PreparedCard = { ...preparedCardData, rotation };
    const playersForUpdate: [Player, Player] = JSON.parse(JSON.stringify(state.players));
    const newPlayers: [Player, Player] = playersForUpdate;
    const attacker = newPlayers.find(p => p.id === playerId)!;
    let newLog = [...state.log];
    const { type } = getCardEffect(preparedCard);


    if (preparedCard.card.essenceCost) {
        if (attacker.essence < preparedCard.card.essenceCost!) {
            return { ...state, log: [...newLog, `${attacker.name} does not have enough essence!`] };
        }
        attacker.essence -= preparedCard.card.essenceCost!;
        newLog.push(`${attacker.name} pays ${preparedCard.card.essenceCost} essence.`);
    }

    const newActivation: ActivationAttempt = {
        initiatorId: playerId,
        attackingCard: preparedCard,
        counterChain: [],
        state: 'PENDING_RESPONSE',
        targetId: playerId === 1 ? 2 : 1, // Target is opponent by default
        resolved: false,
    };

    newLog.push(`t:log.activateCard|playerName=${attacker.name}|cardType=${preparedCard.card.faceSlug || getCardEffect(preparedCard).type}`);

    return { ...state, players: newPlayers, log: newLog, activations: [...state.activations, newActivation], consecutivePasses: 0 };
};

const _counterActivation = (state: GameState, responderId: number, counterCardId: string, rotation: number): GameState => {
    const currentActivation = getTopActivation(state);
    if (!currentActivation) return state;

    // Check if the card is already in ANY active chain
    const allCardsInAllChains = new Set(state.activations.flatMap(act => [act.attackingCard.card.id, ...act.counterChain.map(pc => pc.card.id)]));
    if (allCardsInAllChains.has(counterCardId)) {
        return state;
    }

    const initiatorId = currentActivation.initiatorId;

    const isInitiatorsTurnToRespond = currentActivation.state === 'PENDING_CONFIRMATION' && responderId === initiatorId;
    const isOpponentsTurnToRespond = currentActivation.state === 'PENDING_RESPONSE' && responderId !== initiatorId;

    if (!isInitiatorsTurnToRespond && !isOpponentsTurnToRespond) {
        return state;
    }

    const responderIndex = state.players.findIndex(p => p.id === responderId);
    if (responderIndex === -1) return state;

    const responder = state.players[responderIndex];

    let cardIndexInPrepared = responder.prepared.findIndex(pc => pc.card.id === counterCardId);
    if (cardIndexInPrepared < 0) return state;

    // Create a mutable copy of the game state to work with
    let newPlayers: [Player, Player] = JSON.parse(JSON.stringify(state.players));
    let newResponder = newPlayers[responderIndex];
    let preparedCardData = newResponder.prepared.find(pc => pc.card.id === counterCardId)!;
    let counterCard: PreparedCard = { ...preparedCardData, rotation };

    // Locked Card Logic
    if (preparedCardData.lockedState) {
        const roundsSinceLocked = state.round - preparedCardData.lockedState.appliedRound;

        if (roundsSinceLocked === 0) {
            return { ...state, log: [...state.log, `t:log.lockedCard|playerName=${responder.name}`] };
        }

        // Night 2 onwards: The rotation is fixed.
        counterCard = { ...preparedCardData, rotation: preparedCardData.rotation };
    }

    const { type: counterType } = getCardEffect(counterCard);

    // Enforce Double-Cross rule
    if (counterType === CardType.DoubleCross && currentActivation.initiatorId !== responderId) {
        return { ...state, log: [...state.log, "t:log.doubleCrossInitial"] };
    }

    const targetCard = currentActivation.counterChain.length > 0
        ? currentActivation.counterChain[currentActivation.counterChain.length - 1]
        : currentActivation.attackingCard;

    if (!isValidCounter(counterCard, targetCard)) {
        return { ...state, log: [...state.log, "t:log.invalidCounter"] };
    }

    // Validation passed, now remove the card from the prepared list in our copied state
    const finalCardIndexInPrepared = newResponder.prepared.findIndex(pc => pc.card.id === counterCardId);
    newResponder.prepared.splice(finalCardIndexInPrepared, 1);

    const newTopActivation = {
        ...currentActivation,
        counterChain: [...currentActivation.counterChain, counterCard],
        state: currentActivation.state === 'PENDING_RESPONSE' ? 'PENDING_CONFIRMATION' as const : 'PENDING_RESPONSE' as const
    };

    return {
        ...state,
        players: newPlayers,
        activations: [...state.activations.slice(0, -1), newTopActivation],
        log: [...state.log, `t:log.counterWith|playerName=${responder.name}|cardType=${counterCard.card.faceSlug || getCardEffect(counterCard).type}`]
    };
};

const _acceptActivation = (state: GameState, acceptingPlayerId: number): GameState => {
    const currentActivation = getTopActivation(state);
    if (!currentActivation) return state;

    const initiatorId = currentActivation.initiatorId;
    const responderId = initiatorId === 1 ? 2 : 1;

    const isResponderAccepting = currentActivation.state === 'PENDING_RESPONSE' && acceptingPlayerId === responderId;
    const isInitiatorConfirming = currentActivation.state === 'PENDING_CONFIRMATION' && acceptingPlayerId === initiatorId;

    if (!isResponderAccepting && !isInitiatorConfirming) return state;


    // --- State Setup ---
    let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players));
    let newColorDecks: Record<CardColor, Card[]> = JSON.parse(JSON.stringify(state.colorDecks));
    let newStashDeck = [...state.stashDeck];
    let newStashDiscard = [...state.stashDiscard];
    let newDealingDeck = [...state.dealingDeck];
    let newAvailableTowers = [...state.availableTowers];
    let newDrawEvents = [...state.drawEvents];
    let newEliminatedCards = [...state.eliminatedCards];
    const returnedToColors = new Set<CardColor>();
    let newLog = [...state.log];
    let towerVictor: Player | null = null;
    let finalTargetId: number | undefined = currentActivation.targetId;
    let activationsToPop = 1;
    let chromabowChoicePendingForPlayerId: number | null = null;
    let hibernationChoicePendingForPlayerId: number | null = null;

    // --- Chain Setup ---
    let resolutionChain: PreparedCard[] = [currentActivation.attackingCard, ...currentActivation.counterChain];
    const allChainCards = [...resolutionChain];
    const usedCardIds = new Set<string>(allChainCards.map(pc => pc.card.id));
    const psyBlockedCardIds = new Set<string>();
    const banishedCards: PreparedCard[] = [];
    const eliminatedCards: PreparedCard[] = [];

    // --- Chain Resolution ---
    while (resolutionChain.length >= 2) {
        const counterCard = resolutionChain.pop()!;
        const targetCard = resolutionChain.pop()!;

        const counterEffect = getCardEffect(counterCard);
        const targetEffect = getCardEffect(targetCard);

        const counterOwner = playersCopy.find(p => p.id === counterCard.card.ownerId)!;
        const targetOwner = playersCopy.find(p => p.id === targetCard.card.ownerId)!;


        if (counterEffect.type === CardType.Pushback) {
            targetOwner.hand.push(targetCard.card);
            newLog.push(`${counterOwner.name}'s Pushback returns ${targetOwner.name}'s ${targetEffect.type} to their hand.`);
        } else if (counterEffect.type === CardType.PsyBlock) {
            const existingIndex = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id);

            // The card is locked with the rotation it was played with.
            const lockedCard: PreparedCard = {
                ...targetCard, // targetCard includes the attempted rotation
                lockedState: {
                    appliedRound: state.round,
                }
            };

            if (existingIndex !== -1) {
                targetOwner.prepared[existingIndex] = lockedCard;
            } else {
                targetOwner.prepared.push(lockedCard);
            }

            psyBlockedCardIds.add(targetCard.card.id);
            newLog.push(`${counterOwner.name}'s Psy-Block locks ${targetOwner.name}'s ${targetEffect.type} in place!`);
        } else if (counterEffect.type === CardType.Defense && targetEffect.type === CardType.Attack) {
            const attackValue = targetEffect.value;
            const defenseValue = counterEffect.value;
            if (defenseValue >= attackValue) {
                newLog.push(`${counterOwner.name}'s ${defenseValue} Defense fully blocks the ${attackValue} Attack.`);
            } else {
                newLog.push(`${counterOwner.name}'s ${defenseValue} Defense partially blocks the ${attackValue} Attack.`);
                resolutionChain.push({ ...targetCard, card: { ...targetCard.card, value: attackValue - defenseValue } });
            }
        } else if (counterEffect.type === CardType.Invincible && targetEffect.type === CardType.Attack) {
            newLog.push(`${counterOwner.name}'s Invincible absorbs the ${targetEffect.value} Attack from ${targetOwner.name}.`);
        } else if (counterEffect.type === CardType.Hibernation && targetEffect.type === CardType.Attack) {
            newLog.push(`${counterOwner.name}'s Hibernation absorbs the ${targetEffect.value} Attack from ${targetOwner.name}.`);
            resolutionChain.push(counterCard);
        } else if (counterEffect.type === CardType.Dispel) {
            banishedCards.push(targetCard);
            newLog.push(`${counterOwner.name}'s Dispel banishes ${targetOwner.name}'s ${targetEffect.type}.`);
        } else if (counterEffect.type === CardType.DoubleCross) {
            eliminatedCards.push(targetCard);
            eliminatedCards.push(counterCard);
            newLog.push(`${counterOwner.name}'s Double-Cross eliminates ${targetOwner.name}'s ${targetEffect.type}.`);
        } else if (counterEffect.type === CardType.PlantedBomb) {
            // PlantedBomb neutralizes the target card and survives as the winning card to deal its damage
            newLog.push(`${counterOwner.name}'s Planted Bomb detonates against ${targetOwner.name}'s ${targetEffect.type}!`);
            resolutionChain.push(counterCard);
        }
    }

    // --- Winning Card Effect ---
    if (resolutionChain.length === 1) {
        const winningCard = resolutionChain[0];
        const ownerOfWinningCard = playersCopy.find(p => p.id === winningCard.card.ownerId)!;
        const wasReturnedToHand = ownerOfWinningCard.hand.some(c => c.id === winningCard.card.id);

        if (!wasReturnedToHand) {
            const attacker = playersCopy.find(p => p.id === winningCard.card.ownerId)!;
            if (!finalTargetId) {
                finalTargetId = currentActivation.targetId ?? (attacker.id === 1 ? 2 : 1);
            }
            const defender = playersCopy.find(p => p.id === finalTargetId)!;
            const { type: winningCardType, value: winningCardValue } = getCardEffect(winningCard);

            switch (winningCardType) {
                case CardType.Attack: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s attack is negated by ${defender.name}'s immunity.`);
                    } else {
                        const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalAttackValue = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                            finalAttackValue = Math.max(0, winningCardValue - damageReduction);
                        }

                        const damage = Math.max(0, finalAttackValue - defender.shield);
                        const damageLogs = applyHealthLoss(defender, damage, attacker);
                        defender.shield = Math.max(0, defender.shield - finalAttackValue);
                        newLog.push(`${attacker.name}'s final attack of ${winningCardValue} deals ${damage} damage to ${defender.name}.`);
                        newLog.push(...damageLogs);
                    }
                    break;
                }
                case CardType.InfernoGauntlet: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s Inferno Gauntlet is negated by ${defender.name}'s immunity.`);
                    } else {
                        const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalAttackValue = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                            finalAttackValue = Math.max(0, winningCardValue - damageReduction);
                        }

                        const damageDealt = Math.max(0, finalAttackValue - defender.shield);
                        const damageLogs = applyHealthLoss(defender, damageDealt, attacker);
                        defender.shield = Math.max(0, defender.shield - finalAttackValue);

                        newLog.push(`${attacker.name}'s Inferno Gauntlet deals ${damageDealt} damage to ${defender.name}.`);
                        newLog.push(...damageLogs);

                        if (damageDealt > 0 && defender.hand.length > 0) {
                            const cardsToDiscard = Math.min(2, defender.hand.length);
                            const discardedCards: Card[] = [];

                            const discardSeed = state.seed + `_round${state.round}_inferno_${winningCard.card.id}`;
                            const seedGen = xmur3(discardSeed);
                            const rand = mulberry32(seedGen());

                            for (let i = 0; i < cardsToDiscard; i++) {
                                if (defender.hand.length > 0) {
                                    const randomIndex = Math.floor(rand() * defender.hand.length);
                                    const discardedCard = defender.hand.splice(randomIndex, 1)[0];
                                    discardedCards.push(discardedCard);

                                    if (discardedCard.essenceCost) {
                                        newStashDiscard.push(discardedCard);
                                    } else {
                                        newColorDecks[discardedCard.color].push(discardedCard);
                                        returnedToColors.add(discardedCard.color);
                                    }
                                }
                            }

                            newLog.push(`${defender.name} discards ${discardedCards.length} card(s) from the searing heat!`);
                        }
                    }
                    break;
                }
                case CardType.Larcendagger: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s Larcendagger is negated by ${defender.name}'s immunity.`);
                    } else {
                        const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalAttackValue = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                            finalAttackValue = Math.max(0, winningCardValue - damageReduction);
                        }

                        const damageDealt = Math.max(0, finalAttackValue - defender.shield);
                        const damageLogs = applyHealthLoss(defender, damageDealt, attacker);
                        defender.shield = Math.max(0, defender.shield - finalAttackValue);

                        newLog.push(`${attacker.name}'s Larcendagger deals ${damageDealt} damage to ${defender.name}.`);
                        newLog.push(...damageLogs);

                        if (damageDealt > 0 && defender.hand.length > 0) {
                            const cardsToSteal = Math.min(2, defender.hand.length);
                            const stolenCards: Card[] = [];

                            const stealSeed = state.seed + `_round${state.round}_larcendagger_${winningCard.card.id}`;
                            const seedGen = xmur3(stealSeed);
                            const rand = mulberry32(seedGen());

                            for (let i = 0; i < cardsToSteal; i++) {
                                if (defender.hand.length > 0) {
                                    const randomIndex = Math.floor(rand() * defender.hand.length);
                                    const stolenCard = defender.hand.splice(randomIndex, 1)[0];
                                    stolenCard.ownerId = attacker.id;
                                    attacker.hand.push(stolenCard);
                                    stolenCards.push(stolenCard);
                                }
                            }

                            newLog.push(`${attacker.name} steals ${stolenCards.length} card(s) from ${defender.name}!`);
                        }
                    }
                    break;
                }
                case CardType.LifeCharger: {
                    const healAmount = attacker.maxHealth - attacker.health;
                    attacker.health = attacker.maxHealth;

                    newLog.push(`${attacker.name}'s Life Charger restores ${healAmount} health!`);
                    break;
                }
                case CardType.LifeDrain: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s Life Drain is negated by ${defender.name}'s immunity.`);
                    } else {
                        // Apply damage to defender
                        const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalDrainDamage = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                            finalDrainDamage = Math.max(0, winningCardValue - damageReduction);
                        }

                        const damageDealt = Math.max(0, finalDrainDamage - defender.shield);
                        const damageLogs = applyHealthLoss(defender, damageDealt, attacker);
                        defender.shield = Math.max(0, defender.shield - finalDrainDamage);
                        newLog.push(...damageLogs);

                        // Heal attacker
                        const healAmount = Math.min(winningCardValue, attacker.maxHealth - attacker.health);
                        attacker.health = Math.min(attacker.maxHealth, attacker.health + winningCardValue);

                        newLog.push(`${attacker.name}'s Life Drain deals ${damageDealt} damage and restores ${healAmount} health!`);
                    }
                    break;
                }
                case CardType.NaturalGrip: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s Natural Grip is negated by ${defender.name}'s immunity.`);
                    } else {
                        newLog.push(`${attacker.name}'s Natural Grip takes hold of ${defender.name}!`);
                    }
                    break;
                }
                case CardType.Defense:
                    attacker.shield += winningCardValue;
                    newLog.push(`${attacker.name} gains ${winningCardValue} shield.`);
                    break;
                case CardType.Heal:
                    // Determine heal target (can target self or opponent)
                    const healTarget = playersCopy.find(p => p.id === finalTargetId)!;

                    // First, cure all Disease and Natural Grip effects from the target
                    const diseasesToRemove = healTarget.effectCards.filter(c => c.type === CardType.Disease);
                    const naturalGripsToRemove = healTarget.effectCards.filter(c => c.type === CardType.NaturalGrip);
                    const totalAilments = diseasesToRemove.length + naturalGripsToRemove.length;

                    if (totalAilments > 0) {
                        healTarget.effectCards = healTarget.effectCards.filter(c =>
                            c.type !== CardType.Disease && c.type !== CardType.NaturalGrip
                        );

                        if (diseasesToRemove.length > 0 && naturalGripsToRemove.length > 0) {
                            newLog.push(`${attacker.name}'s healing cures all ${healTarget.name}'s diseases and Natural Grips!`);
                        } else if (diseasesToRemove.length > 0) {
                            newLog.push(`${attacker.name}'s healing cures all ${healTarget.name}'s diseases!`);
                        } else {
                            newLog.push(`${attacker.name}'s healing cures all ${healTarget.name}'s Natural Grips!`);
                        }

                    }

                    // Then apply healing to the target
                    healTarget.health = Math.min(healTarget.maxHealth, healTarget.health + winningCardValue);

                    if (healTarget.id === attacker.id) {
                        newLog.push(`${attacker.name} heals for ${winningCardValue}.`);
                    } else {
                        newLog.push(`${attacker.name} heals ${healTarget.name} for ${winningCardValue}.`);
                    }

                    break;
                case CardType.Steal:
                    if (defender.hand.length > 0) {
                        const stealSeed = state.seed + `_round${state.round}_turn${state.turnPlayerId}_steal_${currentActivation.attackingCard.card.id}`;
                        const seedGen = xmur3(stealSeed);
                        const rand = mulberry32(seedGen());
                        const stolenCardIndex = Math.floor(rand() * defender.hand.length);
                        const [stolenCard] = defender.hand.splice(stolenCardIndex, 1);
                        stolenCard.ownerId = attacker.id;
                        attacker.hand.push(stolenCard);
                        newLog.push(`${attacker.name} steals a card from ${defender.name}!`);
                    }
                    break;
                case CardType.StealTower:
                    if (attacker.isAI) {
                        // AI logic for stealing tower
                        let stolenTowerColor: CardColor | undefined;
                        const stealSeed = state.seed + `_steal_tower_${winningCard.card.id}`;
                        const seedGen = xmur3(stealSeed);
                        const rand = mulberry32(seedGen());

                        if (defender.towers.length > 0) {
                            const stolenIndex = Math.floor(rand() * defender.towers.length);
                            const stolenTower = defender.towers[stolenIndex];
                            stolenTowerColor = stolenTower.color;
                            defender.towers.splice(stolenIndex, 1);
                            attacker.towers.push(stolenTower);
                            newLog.push(`t:log.towerStole|playerName=${attacker.name}|targetColor=${stolenTowerColor}|defenderName=${defender.name}|method=purification`);
                            // Clear towerChangeMenu if it was open for the defender, as indices shifted
                            if (state.towerChangeMenu && state.towerChangeMenu.playerId === defender.id) {
                                state.towerChangeMenu = undefined;
                            }
                        } else if (newAvailableTowers.length > 0) {
                            const stolenIndex = Math.floor(rand() * newAvailableTowers.length);
                            stolenTowerColor = newAvailableTowers[stolenIndex];
                            newAvailableTowers.splice(stolenIndex, 1);
                            const allTowers = [...playersCopy[0].towers, ...playersCopy[1].towers];
                            attacker.towers.push({ color: stolenTowerColor, id: generateTowerId(stolenTowerColor, allTowers) });
                            newLog.push(`t:log.towerClaimed|playerName=${attacker.name}|targetColor=${stolenTowerColor}|method=purification`);
                        } else {
                            newLog.push(`t:log.towerNoTarget|playerName=${attacker.name}`);
                        }
                        if (stolenTowerColor && attacker.towers.length >= 5) towerVictor = attacker;
                    } else {
                        // Player needs to choose a tower
                        newLog.push(`${attacker.name} is choosing a tower to purify...`);
                        return {
                            ...state,
                            players: playersCopy,
                            phase: GamePhase.AWAITING_PURIFICATION_TARGET,
                            purificationInitiatorId: attacker.id,
                            log: newLog,
                            activations: [],
                            turnPlayerId: initiatorId,
                        };
                    }
                    break;
                case CardType.Shock:
                    defender.disruptedSlots = Math.min(defender.maxPrepareSlots, (defender.disruptedSlots + winningCardValue));
                    newLog.push(`${attacker.name} shocks ${defender.name}, locking ${winningCardValue} prep slot(s) next turn!`);
                    break;
                case CardType.Energize:
                case CardType.ArcaneBoots:
                case CardType.Ale:
                    attacker.disruptedSlots -= winningCardValue;
                    const cardName = winningCardType === CardType.Ale ? 'Ale' : (winningCardType === CardType.ArcaneBoots ? 'Arcane Boots' : 'Energize');
                    newLog.push(`${attacker.name}'s ${cardName} grants ${winningCardValue} potential prep slot(s)!`);
                    break;
                case CardType.PsySearch:
                    newLog.push(`${attacker.name} draws ${winningCardValue} card(s).`);

                    // Logic from ensureDealingDeck
                    let availableColors = Object.values(CardColor).filter(color => newColorDecks[color].length > 0);
                    while (newDealingDeck.length < winningCardValue && availableColors.length > 0) {
                        const newCardsForDeck: Card[] = [];
                        availableColors.forEach(color => {
                            const card = newColorDecks[color].pop();
                            if (card) {
                                newCardsForDeck.push(card);
                            }
                        });
                        newDealingDeck = [...newDealingDeck, ...shuffle(newCardsForDeck, state.seed + '_dealing_' + newDealingDeck.length)];
                        availableColors = Object.values(CardColor).filter(color => newColorDecks[color].length > 0);
                    }

                    // Logic from drawFromDealingDeck
                    const cardsToDraw = Math.min(winningCardValue, newDealingDeck.length);
                    for (let i = 0; i < cardsToDraw; i++) {
                        const card = newDealingDeck.pop();
                        if (card) {
                            card.ownerId = attacker.id;
                            attacker.hand.push(card);
                        }
                    }
                    break;
                case CardType.Hibernation:
                    newLog.push(`${attacker.name} is hibernating.`);

                    const availableDrawDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0);

                    if (availableDrawDecks.length > 0) {
                        if (attacker.isAI) {
                            const drawSeed = state.seed + `_hibernation_draw_${winningCard.card.id}`;
                            const seedGen = xmur3(drawSeed);
                            const rand = mulberry32(seedGen());
                            const chosenColor = availableDrawDecks[Math.floor(rand() * availableDrawDecks.length)];

                            const cardToDraw = newColorDecks[chosenColor].pop()!;
                            cardToDraw.ownerId = attacker.id;
                            attacker.hand.push(cardToDraw);
                            newLog.push(`${attacker.name} draws a card from the ${chosenColor} deck from Hibernation.`);
                        } else {
                            hibernationChoicePendingForPlayerId = attacker.id;
                        }
                    }
                    break;
                case CardType.BodyUpgrade:
                    attacker.maxHealth += winningCardValue;
                    attacker.health += winningCardValue;
                    newLog.push(`t:log.bodyUpgradeLog|playerName=${attacker.name}|value=${winningCardValue}`);
                    break;
                case CardType.FortunesFavor:
                    attacker.bonusDraw += winningCardValue;
                    newLog.push(`t:log.fortunesFavorLog|playerName=${attacker.name}`);
                    break;
                case CardType.CursedIron:
                    newLog.push(`t:log.cursedIronLog|playerName=${attacker.name}`);
                    break;
                case CardType.EyesOfMadness:
                    attacker.maxPrepareSlots += winningCardValue;
                    newLog.push(`t:log.eyesOfMadnessLog|playerName=${attacker.name}`);
                    break;
                case CardType.MadnessControl:
                    attacker.maxPrepareSlots += winningCardValue;
                    newLog.push(`t:log.madnessControlLog|playerName=${attacker.name}|value=${winningCardValue}`);
                    break;
                case CardType.BrokenSecret:
                    newLog.push(`t:log.brokenSecretLog|playerName=${attacker.name}|defenderName=${defender.name}`);

                    const colorDeckCardsInHand = defender.hand.filter(c => !c.essenceCost);
                    const cardGroups = new Map<string, Card[]>();

                    colorDeckCardsInHand.forEach(card => {
                        const key = `${card.color}-${card.type}-${card.value}-${card.typeB || ''}-${card.valueB || ''}`;
                        if (!cardGroups.has(key)) {
                            cardGroups.set(key, []);
                        }
                        cardGroups.get(key)!.push(card);
                    });

                    const stolenCards: Card[] = [];
                    cardGroups.forEach((group) => {
                        const sets = Math.floor(group.length / 3);
                        if (sets > 0) {
                            const cardsToSteal = group.slice(0, sets);
                            stolenCards.push(...cardsToSteal);
                        }
                    });

                    if (stolenCards.length > 0) {
                        const stolenCardIds = new Set(stolenCards.map(c => c.id));
                        defender.hand = defender.hand.filter(c => !stolenCardIds.has(c.id));
                        stolenCards.forEach(card => {
                            card.ownerId = attacker.id;
                            attacker.hand.push(card);
                        });
                        newLog.push(`${attacker.name} steals ${stolenCards.length} card(s) from ${defender.name}!`);
                    } else {
                        newLog.push(`t:log.noSetsFoundLog|defenderName=${defender.name}`);
                    }
                    break;
                case CardType.Counterblast:
                case CardType.Invincible: {
                    const isExtendedShieldPlay = state.activations.length > 1 && currentActivation.attackingCard.card.id === winningCard.card.id;

                    // Handle a normally-played Invincible card (not a reaction or extended shield play)
                    if (winningCardType === CardType.Invincible && !isExtendedShieldPlay) {
                        newLog.push(`t:log.invincibleLog|playerName=${attacker.name}`);
                        const invincibleEffectCard: Card = { ...winningCard.card, id: makeDeterministicId(state.seed, `inv_${winningCard.card.id}`, state.round), type: CardType.Invincible, appliedRound: state.round, ownerId: attacker.id };
                        attacker.effectCards.push(invincibleEffectCard);
                        break; // Exit the case
                    }

                    // --- Logic for Counterblast or Extended-Shield Invincible ---
                    const mainActivation = state.activations[0];
                    if (!mainActivation || getCardEffect(mainActivation.attackingCard).type !== CardType.Attack) {
                        newLog.push(`t:log.fizzledLog|cardType=${winningCard.card.faceSlug || winningCardType}`);

                        if (winningCardType === CardType.Invincible) {
                            newLog.push(`t:log.invincibleAnywayLog|playerName=${attacker.name}`);
                            const invincibleEffectCard: Card = { ...winningCard.card, id: makeDeterministicId(state.seed, `inv_${winningCard.card.id}`, state.round), type: CardType.Invincible, appliedRound: state.round, ownerId: attacker.id };
                            attacker.effectCards.push(invincibleEffectCard);
                        }
                        break;
                    }

                    const originalAttackingCard = mainActivation.attackingCard;
                    const originalAttackerId = mainActivation.initiatorId;
                    const originalAttacker = playersCopy.find(p => p.id === originalAttackerId)!;

                    if (winningCardType === CardType.Counterblast) {
                        const damageToReflect = getCardEffect(originalAttackingCard).value;
                        const originalAttackerIsImmune = isImmuneToDamage(originalAttacker, state.round);
                        if (originalAttackerIsImmune) {
                            newLog.push(`t:log.immuneLog|playerName=${originalAttacker.name}`);
                        } else {
                            const actualDamage = Math.max(0, damageToReflect - originalAttacker.shield);
                            originalAttacker.health -= actualDamage;
                            originalAttacker.shield = Math.max(0, originalAttacker.shield - damageToReflect);
                            newLog.push(`t:log.counterblastReflect|sourceName=${attacker.name}|damage=${damageToReflect}|targetName=${originalAttacker.name}`);
                        }
                    } else if (winningCardType === CardType.Invincible) {
                        newLog.push(`t:log.invincibleBlockLog|playerName=${attacker.name}`);
                        const invincibleEffectCard: Card = { ...winningCard.card, id: makeDeterministicId(state.seed, `inv_${winningCard.card.id}`, state.round), type: CardType.Invincible, appliedRound: state.round, ownerId: attacker.id };
                        attacker.effectCards.push(invincibleEffectCard);
                    }

                    // Shared logic: Nullify the original attack
                    const originalOwner = playersCopy.find(p => p.id === originalAttackingCard.card.ownerId)!;
                    const cardIdx = originalOwner.prepared.findIndex(pc => pc.card.id === originalAttackingCard.card.id);
                    if (cardIdx > -1) {
                        originalOwner.prepared.splice(cardIdx, 1);
                        if (originalAttackingCard.card.subType) {
                            originalOwner.hand.push(originalAttackingCard.card);
                        } else {
                            newColorDecks[originalAttackingCard.card.color].push(originalAttackingCard.card);
                            returnedToColors.add(originalAttackingCard.card.color);
                        }
                    }

                    activationsToPop = state.activations.length;
                    break;
                }
                case CardType.PlantedBomb: {
                    const bombOwner = playersCopy.find(p => p.id === winningCard.card.ownerId)!;
                    const bombTarget = playersCopy.find(p => p.id !== bombOwner.id)!;

                    const bombTargetIsImmune = isImmuneToDamage(bombTarget, state.round);
                    if (bombTargetIsImmune) {
                        newLog.push(`t:log.bombImmuneLog|playerName=${bombTarget.name}`);
                    } else {
                        const cursedIronCards = bombTarget.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalDamage = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`t:log.bombCursedIronLog|playerName=${bombTarget.name}|reduction=${damageReduction}`);
                            finalDamage = Math.max(0, winningCardValue - damageReduction);
                        }

                        const actualDamage = Math.max(0, finalDamage - bombTarget.shield);
                        bombTarget.health -= actualDamage;
                        bombTarget.shield = Math.max(0, bombTarget.shield - finalDamage);

                        newLog.push(`t:log.bombExplodeLog|bombOwner=${bombOwner.name}|damage=${actualDamage}|targetName=${bombTarget.name}`);
                    }
                    break;
                }
                case CardType.Protector: {
                    const protectorOwner = playersCopy.find(p => p.id === winningCard.card.ownerId)!;
                    const { rotation } = winningCard;
                    const mode = (rotation % 360 + 360) % 360 > 90 ? 'ATTACK' : 'DEFENSE';

                    const isInitialActivation = winningCard.card.id === currentActivation.attackingCard.card.id;

                    // Auto-defense if used as a reaction (counter), otherwise respect chosen mode.
                    if (!isInitialActivation) {
                        protectorOwner.shield += winningCardValue;
                        newLog.push(`t:log.protectorReactionLog|playerName=${protectorOwner.name}|value=${winningCardValue}`);
                    } else {
                        if (mode === 'ATTACK') {
                            const target = playersCopy.find(p => p.id === finalTargetId)!;
                            const targetIsImmune = isImmuneToDamage(target, state.round);

                            if (!targetIsImmune) {
                                const damage = Math.max(0, winningCardValue - target.shield);
                                target.health -= damage;
                                target.shield = Math.max(0, target.shield - winningCardValue);
                                newLog.push(`t:log.protectorAttackLog|playerName=${protectorOwner.name}|targetName=${target.name}|damage=${damage}`);
                            } else {
                                newLog.push(`t:log.protectorNegatedLog|playerName=${protectorOwner.name}|targetName=${target.name}`);
                            }
                        } else { // DEFENSE mode
                            protectorOwner.shield += winningCardValue;
                            newLog.push(`t:log.protectorDefenseLog|playerName=${protectorOwner.name}|value=${winningCardValue}`);
                        }
                    }

                    // Always heal
                    const healAmount = Math.min(1, protectorOwner.maxHealth - protectorOwner.health);
                    if (healAmount > 0) {
                        protectorOwner.health = Math.min(protectorOwner.maxHealth, protectorOwner.health + 1);
                        newLog.push(`t:log.protectorRestoreLog|playerName=${protectorOwner.name}|value=${healAmount}`);
                    }
                    break;
                }
                case CardType.Chromabow: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`t:log.chromabowNegatedLog|playerName=${attacker.name}|defenderName=${defender.name}`);
                    } else {
                        const damage = Math.max(0, winningCardValue - defender.shield);
                        defender.health -= damage;
                        defender.shield = Math.max(0, defender.shield - winningCardValue);
                        newLog.push(`t:log.chromabowDamageLog|playerName=${attacker.name}|damage=${damage}|defenderName=${defender.name}`);

                        const availableColorDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0);

                        if (availableColorDecks.length > 0) {
                            if (attacker.isAI) {
                                const drawSeed = state.seed + `_chromabow_draw_${winningCard.card.id}`;
                                const seedGen = xmur3(drawSeed);
                                const rand = mulberry32(seedGen());
                                const chosenColor = availableColorDecks[Math.floor(rand() * availableColorDecks.length)];

                                const cardToDraw = newColorDecks[chosenColor].pop()!;
                                cardToDraw.ownerId = attacker.id;
                                attacker.hand.push(cardToDraw);
                                newLog.push(`t:log.chromabowDrawLog|playerName=${attacker.name}|color=${chosenColor}`);
                            } else {
                                newLog.push(`${attacker.name}'s Chromabow allows them to draw a card from any color deck...`);
                                chromabowChoicePendingForPlayerId = attacker.id;
                            }
                        } else {
                            newLog.push(`No cards left for Chromabow to draw.`);
                        }
                    }
                    break;
                }
                case CardType.DiscerningEye:
                    // Add a marker card to defender's effectCards to track the effect
                    const discerningEyeMarker: Card = {
                        ...winningCard.card,
                        id: makeDeterministicId(state.seed, `de_${winningCard.card.id}`, state.round),
                        type: CardType.DiscerningEye,
                        appliedRound: state.round,
                        ownerId: attacker.id // Track who cast it
                    };
                    defender.effectCards.push(discerningEyeMarker);

                    newLog.push(`${attacker.name}'s Discerning Eye reveals ${defender.name}'s strategy!`);
                    break;
                case CardType.EnergySword: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s attack is negated by ${defender.name}'s immunity.`);
                    } else {
                        const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                        const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                        let finalAttackValue = winningCardValue;

                        if (damageReduction > 0) {
                            newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                            finalAttackValue = Math.max(0, winningCardValue - damageReduction);
                        }

                        const damage = Math.max(0, finalAttackValue - defender.shield);
                        defender.health -= damage;
                        defender.shield = Math.max(0, defender.shield - finalAttackValue);
                        newLog.push(`${attacker.name}'s Energy Sword deals ${damage} damage to ${defender.name}.`);
                    }
                    const energizeValue = 2;
                    attacker.disruptedSlots -= energizeValue;
                    newLog.push(`${attacker.name}'s Energy Sword grants ${energizeValue} potential prep slot(s)!`);
                    break;
                }
                case CardType.FlyingScythe: {
                    const defenderIsImmune = isImmuneToDamage(defender, state.round);
                    if (defenderIsImmune) {
                        newLog.push(`${attacker.name}'s Flying Scythe is negated by ${defender.name}'s immunity.`);
                        break;
                    }

                    // --- Base Damage ---
                    const cursedIronCards = defender.effectCards.filter(c => c.type === CardType.CursedIron);
                    const damageReduction = cursedIronCards.reduce((acc, card) => acc + (card.value || 0), 0);
                    let finalBaseAttackValue = winningCardValue;

                    if (damageReduction > 0) {
                        newLog.push(`${defender.name}'s Cursed Iron reduces damage by ${damageReduction}.`);
                        finalBaseAttackValue = Math.max(0, winningCardValue - damageReduction);
                    }

                    const baseDamageDealt = Math.max(0, finalBaseAttackValue - defender.shield);
                    defender.health -= baseDamageDealt;
                    defender.shield = Math.max(0, defender.shield - finalBaseAttackValue);
                    newLog.push(`${attacker.name}'s Flying Scythe strikes for ${finalBaseAttackValue} damage.`);

                    // --- Bonus Damage ---
                    const missingHealth = defender.maxHealth - defender.health;
                    if (missingHealth > 0) {
                        const bonusDamage = Math.floor(missingHealth / 2);
                        if (bonusDamage > 0) {
                            const bonusDamageDealt = Math.max(0, bonusDamage - defender.shield);
                            defender.health -= bonusDamageDealt;
                            defender.shield = Math.max(0, defender.shield - bonusDamage);
                            newLog.push(`The Scythe feeds on weakness, dealing an extra ${bonusDamage} damage!`);
                        }
                    }
                    break;
                }
                case CardType.Frost: {
                    newLog.push(`${attacker.name}'s Frost envelops ${defender.name} in ice!`);

                    const hibernationEffectCard: Card = {
                        id: makeDeterministicId(state.seed, `frost_${winningCard.card.id}`, state.round),
                        type: CardType.Hibernation,
                        value: 1,
                        color: CardColor.Pink,
                        ownerId: defender.id,
                    };
                    defender.effectCards.push(hibernationEffectCard);

                    const availableDrawDecks = Object.values(CardColor).filter(color => newColorDecks[color]?.length > 0);

                    if (availableDrawDecks.length > 0) {
                        if (defender.isAI) {
                            const drawSeed = state.seed + `_frost_draw_${winningCard.card.id}`;
                            const seedGen = xmur3(drawSeed);
                            const rand = mulberry32(seedGen());
                            const chosenColor = availableDrawDecks[Math.floor(rand() * availableDrawDecks.length)];

                            const cardToDraw = newColorDecks[chosenColor].pop()!;
                            cardToDraw.ownerId = defender.id;
                            defender.hand.push(cardToDraw);
                            newLog.push(`${defender.name} draws a card from the ${chosenColor} deck due to Frost.`);
                        } else {
                            hibernationChoicePendingForPlayerId = defender.id;
                        }
                    }
                    break;
                }
            }
        }
    } else {
        newLog.push('The action was fully countered.');
    }

    // Check for Life Charger auto-trigger before checking game over
    for (let i = 0; i < playersCopy.length; i++) {
        const player = playersCopy[i];
        if (player.health <= 0) {
            const stateWithLifeCharger = checkAndTriggerLifeCharger(
                { ...state, players: playersCopy, colorDecks: newColorDecks, stashDeck: newStashDeck, stashDiscard: newStashDiscard, dealingDeck: newDealingDeck, availableTowers: newAvailableTowers, drawEvents: newDrawEvents, eliminatedCards: newEliminatedCards, log: newLog },
                player.id
            );
            if (stateWithLifeCharger.players[i].health > 0) {
                playersCopy = stateWithLifeCharger.players;
                newLog = stateWithLifeCharger.log;
                newStashDiscard = stateWithLifeCharger.stashDiscard;
            }
        }
    }

    // --- Cleanup Phase ---
    playersCopy.forEach(player => {
        // Filter the prepared list. We want to:
        // 1. Keep cards that were NOT used in the chain.
        // 2. For cards that WERE used, remove them, UNLESS they were Psy-Blocked.
        // 3. If a card was Psy-Blocked, remove its original version and only keep the new version with the `lockedState`.
        player.prepared = player.prepared.filter(pc => {
            const wasUsed = usedCardIds.has(pc.card.id);
            const wasPsyBlocked = psyBlockedCardIds.has(pc.card.id);

            if (!wasUsed) {
                return true; // Keep unused card
            }

            if (wasPsyBlocked) {
                // This card was used and targeted by Psy-Block. It should remain in the prepared list.
                return true;
            }

            // Card was used but not Psy-Blocked, so remove it from prepared.
            return false;
        });

        const cardsUsedByThisPlayer = allChainCards.filter(rc => rc.card.ownerId === player.id);

        cardsUsedByThisPlayer.forEach(resolvedCard => {
            // Skip Psy-Blocked cards, as they are now locked in 'prepared'
            if (psyBlockedCardIds.has(resolvedCard.card.id)) {
                return;
            }
            // Skip cards that were returned to hand (by Pushback)
            if (player.hand.some(c => c.id === resolvedCard.card.id)) {
                return;
            }
            // Handle eliminated and banished cards
            if (eliminatedCards.some(c => c.card.id === resolvedCard.card.id)) {
                newEliminatedCards.push(resolvedCard.card);
                return;
            }
            if (banishedCards.some(c => c.card.id === resolvedCard.card.id)) {
                if (resolvedCard.card.subType === 'Spell') newStashDeck.push(resolvedCard.card);
                else {
                    newColorDecks[resolvedCard.card.color].push(resolvedCard.card);
                    returnedToColors.add(resolvedCard.card.color);
                }
                return;
            }

            const cardEffect = getCardEffect(resolvedCard);
            // Handle reminder/effect cards that stay on the field
            // NOTE: Disease, NaturalGrip, VoodooDoll, SealOfSilence, Frost, Hibernation, Invincible, BodyUpgrade, EyesOfMadness, MadnessControl, CursedIron, FortunesFavor
            // are NOW handled in activations_new_function.ts with proper ID preservation for animations.
            // Skip them here to avoid duplicate handling.
            const handledInNewSystem = [
                CardType.Disease,
                CardType.NaturalGrip,
                CardType.VoodooDoll,
                CardType.SealOfSilence,
                CardType.Frost,
                CardType.Hibernation,
                CardType.Invincible,
                CardType.BodyUpgrade,
                CardType.EyesOfMadness,
                CardType.MadnessControl,
                CardType.CursedIron,
                CardType.FortunesFavor,
                CardType.DiscerningEye
            ];
            if (handledInNewSystem.includes(cardEffect.type)) {
                // Already handled in activations_new_function.ts - skip to avoid duplicate effectCards entry
                return;
            }
            if (cardEffect.type === CardType.FortunesFavor || cardEffect.type === CardType.CursedIron || cardEffect.type === CardType.EyesOfMadness || cardEffect.type === CardType.MadnessControl || cardEffect.type === CardType.BodyUpgrade) {
                player.effectCards.push(resolvedCard.card);
                return;
            }
            // Handle stash cards, which return to hand
            if (resolvedCard.card.essenceCost) {
                player.hand.push(resolvedCard.card);
                return;
            }
            // Default: return regular cards to their color deck
            newColorDecks[resolvedCard.card.color].push(resolvedCard.card);
            returnedToColors.add(resolvedCard.card.color);
        });
    });

    returnedToColors.forEach(color => {
        newColorDecks[color] = shuffle(newColorDecks[color], state.seed + `_accept_shuffle_${color}_` + state.log.length);
    });

    const newActivations = state.activations.slice(0, -activationsToPop);

    if (towerVictor) {
        return {
            ...state,
            players: playersCopy,
            phase: GamePhase.GameOver,
            winner: towerVictor,
            log: [...newLog, `${towerVictor.name} controls all towers and is victorious!`],
            activations: [],
            eliminatedCards: newEliminatedCards,
            pendingVictory: { winnerId: towerVictor.id }
        };
    }


    const finalState = {
        ...state,
        players: playersCopy,
        colorDecks: newColorDecks,
        stashDeck: newStashDeck,
        stashDiscard: newStashDiscard,
        dealingDeck: newDealingDeck,
        availableTowers: newAvailableTowers,
        drawEvents: newDrawEvents,
        log: newLog,
        activations: newActivations,
        turnPlayerId: newActivations.length > 0 ? state.turnPlayerId : initiatorId,
        consecutivePasses: 0,
        eliminatedCards: newEliminatedCards,
    };

    if (chromabowChoicePendingForPlayerId) {
        finalState.phase = GamePhase.AWAITING_CHROMABOW_CHOICE;
        finalState.chromabowInitiatorId = chromabowChoicePendingForPlayerId;
        finalState.activations = []; // The activation is resolved.
    }

    if (hibernationChoicePendingForPlayerId) {
        finalState.phase = GamePhase.AWAITING_HIBERNATION_CHOICE;
        finalState.hibernationInitiatorId = hibernationChoicePendingForPlayerId;
        finalState.activations = []; // The activation is resolved.
    }

    return finalState;
};

const endOfCombatRoutine = (state: GameState): GameState => {

    let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players));
    let [p1, p2] = playersCopy;
    let newLog = [...state.log, `The night ends.`];

    let winner = null;
    let phase = GamePhase.RoundEnd;

    // Check for Life Charger auto-triggers before declaring winner
    for (let i = 0; i < playersCopy.length; i++) {
        const player = playersCopy[i];
        if (player.health <= 0) {
            const stateBeforeCheck = { ...state, players: playersCopy, log: newLog };
            const stateAfterCheck = checkAndTriggerLifeCharger(stateBeforeCheck, player.id);
            if (stateAfterCheck.players[i].health > 0) {
                playersCopy = stateAfterCheck.players;
                newLog = stateAfterCheck.log;
            }
        }
    }
    [p1, p2] = playersCopy; // Refresh references

    // Now check for winner after Life Charger checks
    if (p1.health <= 0 || p2.health <= 0) {
        if (p1.health <= 0 && p2.health <= 0) {
            winner = null;
            newLog.push('It\'s a draw!');
        } else if (p1.health <= 0) {
            winner = p2;
            newLog.push(`${p2.name} is victorious!`);
        } else {
            winner = p1;
            newLog.push(`${p1.name} is victorious!`);
        }
        phase = GamePhase.GameOver;
    }

    // --- AXEL ABILITY: Copy returned color deck cards ---
    let newColorDecks: Record<CardColor, Card[]> = JSON.parse(JSON.stringify(state.colorDecks));

    const allPreparedCards = [...p1.prepared, ...p2.prepared];
    allPreparedCards.forEach(pc => {
        const owner = playersCopy.find(p => p.id === pc.card.ownerId);
        // Only return non-locked cards
        if (owner && !pc.lockedState) {
            if (!owner.hand.some(c => c.id === pc.card.id)) {
                owner.hand.push(pc.card);
            }

            // Axel ability: for each returned COLOR DECK card, find a copy in the deck
            const opponent = playersCopy.find(p => p.id !== owner.id)!;
            const effectiveHero = getEffectiveHero(owner, opponent);
            const isColorDeckCard = !pc.card.essenceCost;

            if (effectiveHero === 'AXEL' && isColorDeckCard) {
                const deck = newColorDecks[pc.card.color];
                const copyIndex = deck.findIndex(c => c.type === pc.card.type);

                if (copyIndex !== -1) {
                    const [copy] = deck.splice(copyIndex, 1);
                    copy.ownerId = owner.id;
                    owner.hand.push(copy);
                    newColorDecks[pc.card.color] = shuffle(deck, state.seed + `_axel_${pc.card.id}`);
                    newLog.push(`t:log.axelAbility|playerName=${owner.name}|cardType=${pc.card.faceSlug || pc.card.type}`);
                }
            }
        } else if (owner && pc.lockedState) {
        }
    });
    // --- END AXEL ABILITY ---

    p1.prepared = p1.prepared.filter(pc => pc.lockedState);
    p2.prepared = p2.prepared.filter(pc => pc.lockedState);
    p1.resolved = []; p2.resolved = [];


    const today = new Date()
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    let battleHistory = state.battleHistory ? [...state.battleHistory] : []
    let lastFameDelta = 0
    const isAIGame = playersCopy[0].isAI || playersCopy[1].isAI
    if (winner && !isAIGame) {
        const p1 = playersCopy[0]
        const p2 = playersCopy[1]
        const w = winner.id === p1.id ? p1 : p2
        const l = winner.id === p1.id ? p2 : p1
        const newHistoryEntry = {
            id: state.round,
            date: dateStr,
            opponentName: l.name,
            result: 'win' as const,
            fameDelta: 0,
            winnerId: winner.id,
            loserId: l.id,
        }
        const priorHistoryAsFHGame: FHGame[] = battleHistory
            .filter(g => g.winnerId !== undefined)
            .map(g => ({ id: g.id, date: g.date, winnerId: g.winnerId!, loserId: g.loserId }))
        const gamesP1Today = priorHistoryAsFHGame.filter(g => g.winnerId === p1.id && g.date === dateStr)
        const gamesP2Today = priorHistoryAsFHGame.filter(g => g.winnerId === p2.id && g.date === dateStr)

        const calc = calculateFamePoints({
            player1: { id: p1.id, famePoints: p1.famePoints || 0 },
            player2: { id: p2.id, famePoints: p2.famePoints || 0 },
            winner: { id: w.id, famePoints: w.famePoints || 0 },
            gamesOfBattleHistory: priorHistoryAsFHGame,
            gamesOfPlayer1Today: gamesP1Today,
            gamesOfPlayer2Today: gamesP2Today,
            isInAuthorizedOfflinePlace: false,
            isRecommendedBySystem: false,
        })
        newHistoryEntry.fameDelta = calc.paramR
        battleHistory.push(newHistoryEntry)
        const wi = playersCopy.findIndex(p => p.id === winner!.id)
        playersCopy[wi] = { ...playersCopy[wi], famePoints: calc.newFamePoints }
        newLog.push(`${playersCopy[wi].name} gains ${calc.paramR} fame points.`)
        lastFameDelta = calc.paramR
        const pv = { winnerId: winner!.id }
        state = { ...state, lastFameDelta, pendingVictory: pv }
    } else {
    }
    try {
        const enriched = battleHistory.map(g => ({ ...g, player1Name: playersCopy[0].name, player2Name: playersCopy[1].name }))
        localStorage.setItem('battleHistory', JSON.stringify(enriched))
    } catch { }
    return {
        ...state,
        players: playersCopy,
        colorDecks: newColorDecks,
        log: newLog,
        phase,
        winner,
        drawEvents: state.drawEvents,
        discardMode: null,
        activations: [],
        battleHistory,
    };
};

export const passTurn = (state: GameState, playerId: number): GameState => {
    if (state.turnPlayerId !== playerId || state.activations.length > 0) return state;

    const playerIndex = state.players.findIndex(p => p.id === playerId);
    if (playerIndex === -1) return state;

    const newPlayers = [...state.players] as [Player, Player];
    const player = newPlayers[playerIndex];
    if (!player.hasPassedActiveTurn) {
        newPlayers[playerIndex] = { ...player, hasPassedActiveTurn: true };
    } else {
    }

    const newConsecutivePasses = state.consecutivePasses + 1;

    if (newConsecutivePasses >= 2) {
        return endOfCombatRoutine({ ...state, players: newPlayers });
    }

    const nextPlayerId = playerId === 1 ? 2 : 1;

    return {
        ...state,
        players: newPlayers,
        turnPlayerId: nextPlayerId,
        consecutivePasses: newConsecutivePasses,
        discardMode: null,
        log: [...state.log, `${state.players.find(p => p.id === playerId)!.name} passes.`]
    };
};

export { startNewRound } from './phases';

export { startTowerPurchase } from './towers';

export { cancelTowerPurchase } from './towers';

export { purchaseTower } from './towers';

export { chooseChromabowColor } from './choices';

export { chooseHibernationColor } from './choices';

export { chooseBurglaryCard } from './choices';

// FIX: Added missing functions required by App.tsx
export { getTowerCost } from './towers';

export { choosePurificationTarget } from './towers';

export { toggleDiscardMode } from './discard';

export { discardCardForEssence } from './discard';

export { discardStashCardForEssence } from './discard';
export { discardPreparedColorCardForEssence } from './discard';

export { openTowerChangeMenu } from './towers';

export { closeTowerChangeMenu } from './towers';

export { getAvailableColorsForTowerChange } from './towers';

export { changeTowerColor } from './towers';


export type { AIAction } from './ai';

export { runAIDayActions } from './ai';

export { runAIPreparationTurn } from './ai';
export { chooseBestStashCardId } from './ai';


// FIX: Restructure function with if/else to ensure all code paths return a value
// and separate response logic from initiator logic. Also fixes a broken log statement.
export { getBestAIAction } from './ai';
const getTopActivation = (state: GameState): ActivationAttempt | undefined => {
    return state.activations.length > 0 ? state.activations[state.activations.length - 1] : undefined;
};
export { assignVictoryPoint } from './phases';



export { xmur3, mulberry32 } from './random';

export { attemptActivation, attemptShieldActivation, counterActivation, acceptActivation, astridHeroBlast } from './activations';

export { computeStateChecksum } from './debug';

export const completeBrokenSecret = (state: GameState): GameState => {
    if (state.phase !== GamePhase.BROKEN_SECRET_REVEAL || !state.brokenSecretState) return state;

    const { initiatorId, targetId, stolenCardIds } = state.brokenSecretState;
    const initiatorIndex = state.players.findIndex(p => p.id === initiatorId);
    const targetIndex = state.players.findIndex(p => p.id === targetId);

    if (initiatorIndex === -1 || targetIndex === -1) return state;

    let newState = { ...state };
    const initiator = { ...newState.players[initiatorIndex] };
    const target = { ...newState.players[targetIndex] };

    const stolenIdsSet = new Set(stolenCardIds);
    const stolenCards: Card[] = [];

    // Separate stolen cards from target hand
    target.hand = target.hand.filter(c => {
        if (stolenIdsSet.has(c.id)) {
            stolenCards.push(c);
            return false;
        }
        return true;
    });

    // Move to initiator hand
    stolenCards.forEach(c => {
        c.ownerId = initiatorId;
        initiator.hand.push(c);
    });

    // Handle Animation of Steal (optional logic for history or log)
    if (stolenCards.length > 0) {
        newState.log = [...newState.log, `${initiator.name} stole ${Math.floor(stolenCards.length / 3)} set(s) of secrets from ${target.name}!`];
    } else {
        newState.log = [...newState.log, `${initiator.name} found no secrets.`];
    }

    // Update players
    const newPlayers = [...newState.players] as [Player, Player];
    newPlayers[initiatorIndex] = initiator;
    newPlayers[targetIndex] = target;

    newState.players = newPlayers;
    newState.brokenSecretState = undefined;

    // Resume game flow
    // We were likely in Combat (applying an effect). We should return to Combat.
    // However, if there are pending activations or we need to proceed, we just ensure phase is correct.
    newState.phase = GamePhase.Combat;

    // Trigger cleanup or next activation if needed
    // Assuming this was triggered from an activation resolution flow that was paused.
    // If we just return to Combat, the game loop (finalResolution) might need to be re-checked or continued.
    // Since BrokenSecret is an Effect, it resolved, and now we are just cleaning up the UI phase.
    // The activation itself should have been marked resolved or we need to ensure the stack is processed.
    // For now, setting to Combat allows normal flow.

    return newState;
};
